import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, CheckCircle2, AlertTriangle, Clock, Ban, Wallet, TrendingUp, Target } from "lucide-react";
import { AdminHeader } from "../../components/admin/AdminLayout";
import { StatCard } from "../../components/admin/Badges";
import { api, brl, dateBR, formatError, monthLabel } from "../../lib/api";

function MiniList({ title, items, empty, render, testId }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-bold">{title}</h3>
        <Link to="/admin/mensalidades" className="text-xs font-semibold text-[#166534] hover:underline">Ver tudo</Link>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-neutral-500">{empty}</p>
      ) : (
        <ul className="divide-y divide-neutral-100">{items.slice(0, 6).map(render)}</ul>
      )}
    </div>
  );
}

export default function Overview() {
  const [s, setS] = useState(null);
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/admin/stats"), api.get("/admin/billing")])
      .then(([a, b]) => {
        setS(a.data);
        setBilling(b.data);
      })
      .catch((e) => toast.error(formatError(e)));
  }, []);

  const v = (k) => (s ? s[k] : "—");
  return (
    <div data-testid="admin-overview">
      <AdminHeader
        title="Dashboard"
        subtitle={s ? `Mensalidade R$ 30,00 a cada ${s.cycle_days} dias · mês de referência: ${monthLabel(s.current_month)}` : "Carregando…"}
      />
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard icon={Users} label="TOTAL" value={v("total_members")} tone="primary" testId="stat-total" />
        <StatCard icon={CheckCircle2} label="ATIVOS EM DIA" value={v("active_members")} tone="ok" testId="stat-active" />
        <StatCard icon={AlertTriangle} label="VENCIDOS" value={v("overdue_members")} tone="err" testId="stat-overdue" />
        <StatCard icon={Clock} label="AGUARDANDO PAGAMENTO" value={v("pending_members")} tone="warn" testId="stat-pending" />
        <StatCard icon={Ban} label="BLOQUEADOS" value={v("blocked_members")} testId="stat-blocked" />
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={Wallet} label="RECEBIDO NO MÊS" value={s ? brl(s.received_this_month) : "—"} testId="stat-received-month" />
        <StatCard icon={Target} label="PREVISTO NO MÊS" value={s ? brl(s.expected_this_month) : "—"} testId="stat-expected-month" />
        <StatCard icon={TrendingUp} label="RECEBIDO (TOTAL)" value={s ? brl(s.received_total) : "—"} testId="stat-received-total" />
      </div>
      {billing && (
        <div className="mt-6 grid md:grid-cols-2 gap-4">
          <MiniList
            title="Pagamentos recentes do mês"
            items={billing.paid}
            empty="Nenhum pagamento neste mês."
            testId="overview-paid-list"
            render={(p) => (
              <li key={p.id} className="py-2 flex justify-between text-sm">
                <span><span className="font-mono text-[#166534] mr-2">{p.cadastro_number}</span>{p.member_name}</span>
                <span className="text-neutral-500">{dateBR(p.paid_at)}</span>
              </li>
            )}
          />
          <MiniList
            title="Mensalidades vencidas"
            items={billing.overdue}
            empty="Nenhum associado vencido."
            testId="overview-unpaid-list"
            render={(u) => (
              <li key={u.member_id} className="py-2 flex justify-between text-sm">
                <span><span className="font-mono text-[#166534] mr-2">{u.cadastro_number}</span>{u.member_name}</span>
                <span className="text-rose-600">venc. {dateBR(u.due_date)}</span>
              </li>
            )}
          />
        </div>
      )}
    </div>
  );
}
